import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Quote, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Marquee } from '@/components/generals/marquee'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { Sticker } from '@/components/generals/sticker'
import { FEATURED_TESTIMONIAL, TESTIMONIALS } from '@/constants/home'

const initials = (name: string): string => {
    return name.split(' ').map((part) => part[0]).join('').slice(0, 2)
}

const Stars: FC = (): ReactNode => {
    return (
        <div className='flex gap-1' role='img' aria-label='Rated 5 out of 5'>
            {[0, 1, 2, 3, 4].map((index) => (
                <Star key={index} className='size-4 fill-plum text-plum transition-transform duration-300 group-hover/card:scale-125' style={{ transitionDelay: `${index * 50}ms` }} aria-hidden='true' />
            ))}
        </div>
    )
}

export const Testimonials: FC = (): ReactNode => {
    return (
        <section id='reviews' aria-labelledby='reviews-title' className='relative bg-linear-to-b from-lavender to-bubblegum py-24 sm:py-32'>
            <Scallop className='text-lavender' />

            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='reviews-title'
                    lines={['Kind words from', 'very good people']}
                    sticker='Reviews'
                    stickerShape='flower'
                    stickerColor='text-butter'
                />
            </div>

            <Marquee label='reviews' duration='60s' className='mt-14 pb-14' controlClassName='right-5 sm:right-8'>
                <ul className='flex gap-5 py-3 pr-5'>
                    {TESTIMONIALS.map((testimonial) => (
                        <li key={testimonial.name} className='group/card w-[min(82vw,22rem)] shrink-0'>
                            <figure className={cn('flex h-full flex-col rounded-[2rem] border-2 border-plum p-7 shadow-[0_5px_0_0_var(--color-plum)] transition-[translate,rotate,box-shadow] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-2 hover:-rotate-2 hover:shadow-[0_10px_0_0_var(--color-plum)]', testimonial.color)}>
                                <Stars />

                                <blockquote className='mt-5 flex-1 text-lg leading-relaxed text-plum'>
                                    <p>{testimonial.quote}</p>
                                </blockquote>

                                <figcaption className='mt-6 flex items-center gap-3'>
                                    <span aria-hidden='true' className='grid size-11 shrink-0 place-items-center rounded-full border-2 border-plum bg-cream font-heading text-lg text-plum'>
                                        {initials(testimonial.name)}
                                    </span>

                                    <span>
                                        <span className='block font-heading text-xl leading-tight text-plum'>
                                            {testimonial.name}
                                        </span>

                                        <span className='block text-sm text-plum/70'>
                                            {testimonial.role}
                                        </span>
                                    </span>
                                </figcaption>
                            </figure>
                        </li>
                    ))}
                </ul>
            </Marquee>

            <div className='mx-auto mt-4 max-w-6xl px-5 sm:px-8'>
                <figure className='reveal group grid overflow-hidden rounded-[2.5rem] border-2 border-plum bg-cream shadow-[0_6px_0_0_var(--color-plum)] md:grid-cols-[0.9fr_1.1fr]'>
                    <div className='relative min-h-80 border-b-2 border-plum md:border-r-2 md:border-b-0'>
                        <Image
                            src={FEATURED_TESTIMONIAL.image}
                            alt={FEATURED_TESTIMONIAL.imageAlt}
                            fill
                            sizes='(min-width: 768px) 520px, 100vw'
                            className='object-cover object-[60%_30%] transition-transform duration-700 ease-out group-hover:scale-105'
                        />

                        <Sticker
                            label='Client story'
                            shape='blob'
                            colorClassName='text-butter'
                            className='absolute top-5 left-5 size-24 -rotate-12 text-base motion-safe:animate-float'
                        />
                    </div>

                    <div className='flex flex-col justify-center p-7 sm:p-12'>
                        <Quote className='size-12 origin-bottom-left fill-coral text-coral transition-transform duration-500 group-hover:-rotate-12 group-hover:scale-125' aria-hidden='true' />

                        <blockquote className='mt-5 font-heading text-[clamp(1.6rem,2.8vw,2.4rem)] leading-[1.15] text-plum'>
                            <p>{FEATURED_TESTIMONIAL.quote}</p>
                        </blockquote>

                        <figcaption className='mt-7'>
                            <span className='block font-heading text-2xl text-plum'>
                                {FEATURED_TESTIMONIAL.name}
                            </span>

                            <span className='block text-plum/70'>
                                {FEATURED_TESTIMONIAL.role}
                            </span>
                        </figcaption>

                        <Link
                            href={`/work/${FEATURED_TESTIMONIAL.projectSlug}`}
                            className='mt-7 inline-flex w-fit items-center gap-2 font-heading text-lg text-plum underline decoration-2 underline-offset-4 hover:decoration-coral'
                        >
                            Read the Bluebird story
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                </figure>
            </div>
        </section>
    )
}
